import React from "react";
import { Link } from "react-router-dom";
import {
  Building, Hotel, Home as HomeIcon, Warehouse, Factory, Stethoscope,
  ShoppingBag, Utensils, GraduationCap, Plane, Tv, Truck,
  Briefcase, Wrench, Beaker, Layers,
  ArrowRight, Shield, TrendingUp,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import useSEO from "@/lib/useSEO";

const BONUS_TABLE = [
  { year: 2017, pct: 50, note: "Pre-TCJA rate" },
  { year: 2018, pct: 100, note: "TCJA full bonus" },
  { year: 2019, pct: 100 },
  { year: 2020, pct: 100 },
  { year: 2021, pct: 100 },
  { year: 2022, pct: 100, note: "Last year of original full bonus" },
  { year: 2023, pct: 80, note: "TCJA phase-down begins" },
  { year: 2024, pct: 60 },
  { year: 2025, pct: 100, note: "OBBBA restored 100% — permanent" },
  { year: 2026, pct: 100, note: "OBBBA — permanent" },
  { year: 2027, pct: 100, note: "OBBBA — permanent" },
];

const INDUSTRIES = [
  { i: Building, l: "Office Buildings" },
  { i: ShoppingBag, l: "Retail Centers" },
  { i: Factory, l: "Manufacturing" },
  { i: Warehouse, l: "Warehouse & Distribution" },
  { i: Hotel, l: "Hotels & Hospitality" },
  { i: HomeIcon, l: "Multifamily / Apartments" },
  { i: Stethoscope, l: "Medical & Dental" },
  { i: GraduationCap, l: "Education" },
  { i: Utensils, l: "Restaurants" },
  { i: Plane, l: "Airports & Aviation" },
  { i: Wrench, l: "Auto Dealerships & Service" },
  { i: Beaker, l: "Laboratories" },
  { i: Truck, l: "Self-Storage" },
  { i: Briefcase, l: "Professional Offices" },
  { i: Tv, l: "Data Centers" },
  { i: Layers, l: "Mixed-Use Properties" },
];

const QUALIFY = [
  { t: "Short-Term Rentals (STR)", d: "Airbnb, VRBO, and other STR properties used <7 days average stay can qualify for material participation, unlocking deductions against active income." },
  { t: "Long-Term Rentals", d: "Single-family rentals, duplexes, and small portfolios held for passive income — accelerates depreciation against rental income." },
  { t: "Multifamily", d: "5+ unit apartment buildings often see the largest dollar impact — 25–35% of basis frequently reclassifies into 5/7/15-year property." },
  { t: "Commercial Building Owners", d: "Office, retail, industrial, medical — any non-residential income property with depreciable basis above ~$500K." },
  { t: "Portfolios", d: "Real estate funds, family offices, and PE — portfolio-wide rollouts with consolidated reporting and pricing." },
];

const STEPS = [
  { n: "01", t: "Free Preliminary Review", d: "We model expected savings at no cost. If the math doesn't comfortably exceed the fee, we tell you up front." },
  { n: "02", t: "Detailed Engineering Report", d: "Our engineers analyze blueprints, invoices, and site data. We walk the property, identify every reclassifiable component, and build a defensible asset schedule." },
  { n: "03", t: "Report Delivery", d: "You receive a complete engineering report documenting every reclassified asset with photos, citations, and depreciation tables — audit-ready." },
  { n: "04", t: "CPA Coordination", d: "We work directly with your CPA or accountant — handing them everything they need to apply the deductions on your return." },
  { n: "05", t: "Form 3115 In-House", d: "For look-back studies, we prepare and file Form 3115 in-house — no amended returns required, and the catch-up deduction lands in a single year." },
];

const CASES = [
  { ptype: "Multifamily — 240 units", price: "$28.4M", year: "2023", saved: "$2,140,000", note: "First-year accelerated depreciation against active K-1 income." },
  { ptype: "Industrial / Manufacturing", price: "$12.0M", year: "2024", saved: "$1,025,000", note: "Reclassified mechanical, electrical, and process equipment." },
  { ptype: "Short-Term Rental Portfolio (8 homes)", price: "$6.8M", year: "2024", saved: "$612,000", note: "Owner qualified as material participant — offset W-2 income." },
  { ptype: "Office Tower", price: "$45.0M", year: "2022", saved: "$3,950,000", note: "Look-back study; catch-up deduction filed via Form 3115." },
];

const fmtUsd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function CostSegregation() {
  useSEO({
    title: "Cost Segregation Studies | Accelerate Depreciation & Cash Flow | IncentTax",
    description:
      "Engineering-based cost segregation studies reclassify 20–35% of your property basis into 5, 7, and 15-year property — unlocking accelerated and bonus depreciation. IRS-compliant, audit-defended, nationwide.",
    canonical: "https://incenttax.com/services/cost-segregation",
  });
  return (
    <PageShell testid="cost-seg-page">
      {/* HERO */}
      <section className="bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <p className="text-brass uppercase tracking-[0.28em] text-sm mb-6">Service · 01</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
            Cost Segregation,
            <br /> <span className="italic text-brass">engineered to the dollar.</span>
          </h1>
          <p className="mt-8 text-ivory/85 text-lg sm:text-xl leading-relaxed max-w-3xl">
            An IRS-sanctioned strategy that reclassifies 20–35% of a building's
            basis from 39-year property into 5, 7, and 15-year property —
            unlocking accelerated and bonus depreciation in year one.
          </p>

          <div className="mt-14 grid sm:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10">
            <Stat k="$50K – $500K+" v="Typical first-year savings range" />
            <Stat k="100% Bonus" v="Restored by OBBBA — permanent for 2025+" />
            <Stat k="Audit Defense" v="Included with every engagement" />
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <a href="/#estimator" className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-8 py-4 font-medium" data-testid="costseg-cta-estimator">
              Run the estimator <ArrowRight size={18} />
            </a>
            <a href="/#contact" className="inline-flex items-center justify-center gap-2 border border-ivory/40 hover:bg-ivory hover:text-forest-900 px-8 py-4 font-medium transition-colors">
              Talk to a sales leader
            </a>
          </div>
        </div>
      </section>

      {/* DEFINITIONS */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">The fundamentals</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            Three concepts you should master before signing a closing statement.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-ivory-400 border border-ivory-400">
            {[
              { t: "Depreciation", d: "The IRS lets you deduct the cost of an income-producing property over its useful life. Residential rental: 27.5 years. Commercial: 39 years. Spread evenly, year by year." },
              { t: "Accelerated Depreciation", d: "Some property components have a shorter useful life: 5 years (furniture, fixtures), 7 years (machinery), 15 years (land improvements). Deducting these on a faster schedule front-loads cash savings." },
              { t: "Cost Segregation", d: "An engineering-based study that breaks a single building purchase into its components — identifying every piece eligible for shorter-life accelerated depreciation. The result: 20–35% of basis moves out of 39-year property." },
            ].map((c) => (
              <div key={c.t} className="bg-white p-10">
                <div className="font-serif text-3xl text-forest-900 mb-4">{c.t}</div>
                <p className="text-charcoal leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAX LIABILITY BREAKDOWN */}
      <section className="py-24 bg-ivory-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Tax liability reduction</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">
              Real numbers on a <span className="italic text-brass">real building.</span>
            </h2>
            <p className="mt-6 text-charcoal leading-relaxed text-lg">
              Sample: a $2.5M multifamily property placed in service in 2025 — under the OBBBA's permanent 100% bonus depreciation.
            </p>
          </div>
          <div className="lg:col-span-7 bg-white border border-ivory-400">
            <Row label="Purchase Price" v={fmtUsd(2500000)} />
            <Row label="Less: Land (20%)" v={`– ${fmtUsd(500000)}`} />
            <Row label="Depreciable Basis" v={fmtUsd(2000000)} bold />
            <Row label="Reclassified to 5/7-yr (~22%)" v={fmtUsd(440000)} />
            <Row label="Reclassified to 15-yr (~10%)" v={fmtUsd(200000)} />
            <Row label="Total Reclassified" v={fmtUsd(640000)} bold />
            <Row label="2025 Bonus Depreciation (100% — OBBBA)" v={fmtUsd(640000)} />
            <Row label="Combined Tax Rate (37% Fed + 5% State)" v="42%" />
            <Row label="Estimated Year-One Cash Savings" v={fmtUsd(268800)} highlight />
          </div>
        </div>
      </section>

      {/* CASH FLOW */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <TrendingUp className="text-brass mb-6" size={36} strokeWidth={1.5} />
            <h2 className="font-serif text-4xl sm:text-5xl text-ivory leading-tight">
              Cash that was going to the IRS — <span className="italic text-brass">redirected to your balance sheet.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 text-ivory/85 text-lg leading-relaxed space-y-5">
            <p>Cost segregation doesn't reduce your total depreciation — it accelerates it. You take more in year one (and the next several), less in years 10+. The time value of that cash is massive.</p>
            <p>Investors typically reinvest the freed cash into the next acquisition, debt reduction, or capital improvements — compounding the IRR of the underlying portfolio.</p>
            <ul className="space-y-2">
              {["Down payment on the next deal", "Principal paydown on existing debt", "Capital improvements / value-add", "Distributions to LPs"].map((x) => (
                <li key={x} className="flex gap-3"><span className="text-brass">▸</span>{x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* BONUS DEPRECIATION TABLE */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Bonus depreciation schedule</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            OBBBA changed everything. <span className="italic text-brass">100% bonus is back — permanently.</span>
          </h2>
          <div className="border border-ivory-400 bg-white">
            <div className="hidden sm:grid grid-cols-12 px-6 py-4 border-b border-ivory-400 text-xs uppercase tracking-[0.2em] text-forest-700">
              <div className="col-span-2">Year</div>
              <div className="col-span-2">Bonus %</div>
              <div className="col-span-8">Visual</div>
            </div>
            {BONUS_TABLE.map((r) => (
              <div key={r.year} className="grid grid-cols-12 px-6 py-4 border-b border-ivory-400 last:border-b-0 items-center">
                <div className="col-span-2 font-serif text-2xl text-forest-900">{r.year}</div>
                <div className="col-span-2 tabular text-charcoal">{r.pct}%</div>
                <div className="col-span-12 sm:col-span-8 mt-2 sm:mt-0">
                  <div className="h-2 bg-ivory-300 relative overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 ${r.year >= 2023 && r.year <= 2026 ? "bg-brass" : r.year === 2017 ? "bg-forest-300" : r.year === 2027 ? "bg-ivory-400" : "bg-forest-700"}`}
                      style={{ width: `${r.pct}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-charcoal text-sm">
            The One Big Beautiful Bill Act (OBBBA), signed in July 2025, permanently restored 100% bonus depreciation for qualified property placed in service in 2025 and beyond. The 80%/60% rates from 2023–2024 remain in effect for those years. Bonus depreciation applies to qualifying short-life property (5/7/15-year) identified through cost segregation.
          </p>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 bg-ivory-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Industries that qualify</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-3xl leading-tight">
            If your building generates income, it probably qualifies.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ivory-400 border border-ivory-400">
            {INDUSTRIES.map(({ i: Icon, l }) => (
              <div key={l} className="bg-white p-6 flex flex-col items-start gap-3 hover:bg-ivory transition-colors">
                <Icon className="text-brass" size={24} strokeWidth={1.5} />
                <div className="text-charcoal font-medium">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO QUALIFIES */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Who qualifies</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            Five investor profiles that benefit most.
          </h2>
          <div className="space-y-px bg-ivory-400 border border-ivory-400">
            {QUALIFY.map((q) => (
              <div key={q.t} className="bg-white p-8 grid md:grid-cols-12 gap-6">
                <div className="md:col-span-4 font-serif text-2xl text-forest-900">{q.t}</div>
                <div className="md:col-span-8 text-charcoal leading-relaxed">{q.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-brass uppercase tracking-[0.24em] text-sm mb-4">Our 5-step process</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-ivory mb-12 max-w-4xl leading-tight">
            How cost segregation works — and how our team supports you.
          </h2>
          <ol className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-ivory/10 border border-ivory/10">
            {STEPS.map((s) => (
              <li key={s.n} className="bg-forest-900 p-8">
                <div className="font-serif text-5xl text-brass mb-4">{s.n}</div>
                <div className="font-serif text-xl text-ivory mb-3">{s.t}</div>
                <p className="text-ivory/75 text-sm leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Case studies</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            Four properties. Four <span className="italic text-brass">real outcomes.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-ivory-400 border border-ivory-400">
            {CASES.map((c, i) => (
              <div key={c.ptype} className="bg-white p-8" data-testid={`case-study-${i}`}>
                <div className="text-forest-700 text-xs uppercase tracking-[0.22em] mb-3">Case · {String(i + 1).padStart(2, "0")}</div>
                <div className="font-serif text-2xl text-forest-900 mb-2">{c.ptype}</div>
                <div className="text-charcoal text-sm mb-6">{c.price} · placed {c.year}</div>
                <div className="border-t border-ivory-400 pt-6 flex items-baseline justify-between">
                  <div>
                    <div className="text-forest-700 text-xs uppercase tracking-[0.22em] mb-1">Year-1 Savings</div>
                    <div className="font-sans text-3xl text-brass tabular">{c.saved}</div>
                  </div>
                </div>
                <p className="text-charcoal text-sm mt-6 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ POINTER */}
      <section className="py-24 bg-ivory-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">FAQ</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">
              Have questions? <span className="italic text-brass">We've answered them.</span>
            </h2>
            <p className="text-charcoal text-lg mt-6 max-w-2xl leading-relaxed">
              Pricing, timing, audits, recapture, the short-term rental loophole, working
              with your CPA — it's all covered in our FAQ library in the Knowledge Center.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/knowledge?tab=faqs"
              className="inline-flex items-center justify-center gap-2 border border-forest-900 text-forest-900 hover:bg-forest-900 hover:text-ivory px-10 py-4 font-medium transition-colors"
              data-testid="costseg-faq-link"
            >
              Browse FAQs <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Shield className="text-brass mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-6">
            Free preliminary review. <span className="italic text-brass">Zero obligation.</span>
          </h2>
          <p className="text-ivory/80 text-lg mb-10 max-w-2xl mx-auto">
            Send us a closing statement or 1098. We'll model your savings and reply within one business day.
          </p>
          <Link to="/#contact" className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium" data-testid="costseg-bottom-cta">
            Request Free Review <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

function Stat({ k, v }) {
  return (
    <div className="bg-forest-900 p-8">
      <div className="font-sans text-3xl sm:text-4xl text-ivory tabular tracking-tight">{k}</div>
      <div className="text-ivory/60 text-xs uppercase tracking-[0.22em] mt-2">{v}</div>
    </div>
  );
}

function Row({ label, v, bold, highlight }) {
  return (
    <div className={`flex items-baseline justify-between px-6 sm:px-8 py-4 border-b border-ivory-400 last:border-b-0 ${highlight ? "bg-forest-900 text-ivory" : ""}`}>
      <span className={`${bold || highlight ? "font-medium" : ""} text-sm sm:text-base`}>{label}</span>
      <span className={`tabular ${highlight ? "text-2xl text-brass" : bold ? "font-medium text-forest-900" : "text-charcoal"}`}>{v}</span>
    </div>
  );
}
